import { Typography } from '@sunwebgroup/uds-ui/typography'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import cn from 'classnames'
import { useRouter } from 'next/router'
import * as React from 'react'
import { useCMSPageField } from '@/cms/fields'
import { useSubscribeParticipant } from '@/domain/groups/hooks'
import { groupBookingsAPIClient } from '@/infrastructure/groups/api'
import { useIntl } from '@/intl'
import { Custom500 } from '@/pages/500'
import { CheckoutHeader } from '@/scopes/checkout/components/CheckoutHeader'
import { Loader, MainContent } from '@/scopes/checkout/components/Layout'
import { useSendCheckoutEvent } from '@/scopes/checkout/domain/analytics/useSendCheckoutEvent'
import { CheckoutReactContext } from '@/scopes/checkout/domain/CheckoutContext'
import { useBookingDraftQuery, useCheckoutSid, useCheckoutSidSession } from '@/scopes/checkout/domain/hooks'
import { PageLayout } from '@/scopes/checkout/groups/components/PageLayout'
import { StatusNotification } from '@/scopes/checkout/groups/components/StatusNotification'
import { GroupsDependenciesProvider } from '@/scopes/checkout/groups/dependencies/context'
import { checkoutAPIClient } from '@/scopes/checkout/infrastructure/api'
import { CheckoutGroupsNotificationError } from '@/scopes/checkout/pages/groups/Components/NotificationError'
import { DevTools } from '@/scopes/common/components/DevTools/DevTools'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { NewRelicErrorBoundary } from '@/scopes/common/infrastructure/logging/NewRelicErrorBoundary'
import createNextRouter from '@/scopes/common/infrastructure/router/nextRouter'
import styles from './pages.module.css'

const queryClient = new QueryClient()
function Page() {
  const sid = useCheckoutSid()
  const { hasChecked } = useCheckoutSidSession()

  useSendCheckoutEvent()

  if (!hasChecked) {
    return (
      <PageLayout>
        <Loader />
      </PageLayout>
    )
  }

  if (!sid) {
    return (
      <PageLayout>
        <CheckoutGroupsNotificationError />
      </PageLayout>
    )
  }

  return <PageContent />
}

function PageContent() {
  const { isLoading, data } = useSubscribeParticipant()
  const { isLoading: isLoadingBooking, data: bookingData } = useBookingDraftQuery()
  const subscriptionModel = bookingData?.groupInformation?.subscriptionModel

  const intl = useIntl()
  const brandName = useConfiguration('brandName')
  const groupsContactInformation = useCMSPageField('Groups.contactInformation') ?? null

  if (isLoading) {
    return <Loader />
  }

  return (
    <PageLayout>
      <NewRelicErrorBoundary scope="groups-page" fallback={<Custom500 brand={brandName} />}>
        <CheckoutHeader showLoginMenu={false} showBackButton={false} />
        <MainContent>
          {isLoadingBooking
            ? (
                <div className={cn(styles.centeredStack)}>
                  <Loader />
                  <Typography className={cn(styles.loadingMessage)} variant="body-m-bold-snug">
                    {intl.formatMessage({ id: 'groups_checkout_steps_confirm_step_loading' })}
                  </Typography>
                </div>
              )
            : (
                <div className={cn(styles.content)}>
                  <div className={cn(styles.primaryContent)}>
                    {data
                      ? (
                          <div className={cn(styles.centeredStack)}>
                            <StatusNotification
                              status={data?.status}
                              groupName={bookingData?.groupInformation?.name}
                              errorReason={data?.errorReason}
                            />
                            {data?.status === 'success'
                              ? (
                                  <>
                                    <Typography>
                                      {intl.formatMessage(
                                        { id: 'groups_checkout_steps_confirm_step_more_description' },
                                        { bookingNumber: data?.childBookingNumber },
                                      )}
                                    </Typography>
                                    <div className={cn(styles.descriptionBlock)}>
                                      <Typography variant="body-m-bold-snug">
                                        {intl.formatMessage({ id: 'groups_checkout_steps_confirm_step_more_info_title' })}
                                      </Typography>
                                      <Typography
                                        variant="body-m-regular-snug"
                                        dangerouslySetInnerHTML={{
                                          __html: intl.formatMessage(
                                            { id: 'groups_checkout_steps_confirm_step_more_info_description_1' },
                                            { groupName: `<strong>${bookingData?.groupInformation?.name}</strong>` },
                                          ),
                                        }}
                                      />
                                      {bookingData?.groupInformation?.selectedRoom?.pinCode
                                      && bookingData?.groupInformation?.selectedRoom?.name && (
                                        <Typography
                                          variant="body-m-regular-snug"
                                          dangerouslySetInnerHTML={{
                                            __html: intl.formatMessage(
                                              { id: 'groups_checkout_steps_confirm_step_more_info_description_2' },
                                              {
                                                pinCode: `<strong>${bookingData?.groupInformation?.selectedRoom?.pinCode}</strong>`,
                                                roomName: `<strong>${bookingData?.groupInformation?.selectedRoom?.name}</strong>`,
                                              },
                                            ),
                                          }}
                                        />
                                      )}
                                      <Typography variant="body-m-bold-snug">
                                        {intl.formatMessage({ id: 'groups_checkout_steps_confirm_step_more_info_title_2' })}
                                      </Typography>
                                      {subscriptionModel === 'ClosedActive'
                                        ? (
                                            <Typography variant="body-m-regular-snug">
                                              {intl.formatMessage({ id: 'groups_checkout_steps_confirm_step_more_info_closed_model' })}
                                            </Typography>
                                          )
                                        : (
                                            <Typography
                                              variant="body-m-regular-snug"
                                              dangerouslySetInnerHTML={{
                                                __html: intl.formatMessage({
                                                  id: 'groups_checkout_steps_confirm_step_more_info_description_6',
                                                }),
                                              }}
                                            />
                                          )}
                                      <Typography variant="body-m-regular-snug">
                                        {intl.formatMessage({ id: 'groups_checkout_steps_confirm_step_more_info_description_3' })}
                                      </Typography>
                                      <Typography variant="body-m-regular-snug">
                                        {intl.formatMessage(
                                          { id: 'groups_checkout_steps_confirm_step_more_info_description_4' },
                                          { groupName: bookingData?.groupInformation?.name },
                                        )}
                                      </Typography>
                                      <Typography variant="body-m-regular-snug">
                                        {intl.formatMessage(
                                          { id: 'groups_checkout_steps_confirm_step_more_info_description_5' },
                                          { contactName: groupsContactInformation?.contactName },
                                        )}
                                      </Typography>
                                      <Typography
                                        variant="body-m-regular-snug"
                                        dangerouslySetInnerHTML={{
                                          __html: intl.formatMessage(
                                            {
                                              id: 'groups_checkout_steps_confirm_step_more_info_description_7',
                                            },
                                            { contactEmail: groupsContactInformation?.contactEmail },
                                          ),
                                        }}
                                      />
                                    </div>
                                  </>
                                )
                              : null}
                          </div>
                        )
                      : (
                          ''
                        )}
                  </div>
                </div>
              )}
        </MainContent>
      </NewRelicErrorBoundary>
    </PageLayout>
  )
}

export default function ConfirmationStep() {
  const router = useRouter()
  const contextItemId = useCMSPageField('contextItemId')
  const [sid, setSid] = React.useState<string | null>(null)
  const checkoutContextValue = React.useMemo(() => ({
    contextItemId,
    draftId: sid || null,
    setDraftId: setSid,
    step: 4,
  }), [contextItemId, sid])

  return (
    <QueryClientProvider client={queryClient}>
      <CheckoutReactContext value={checkoutContextValue}>
        <GroupsDependenciesProvider
          dependencies={{ checkoutAPIClient, groupBookingsAPIClient, router: createNextRouter(router) }}
        >
          <Page />
          <DevTools />
        </GroupsDependenciesProvider>
      </CheckoutReactContext>
    </QueryClientProvider>
  )
}
